/**
 * useOrderSound — Plays a unique "order placed" success sound
 * Custom synthesized sound using Web Audio API
 * Inspired by payment success chimes but totally unique
 */

export function playOrderSuccessSound(): void {
  try {
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0, ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(0.7, ctx.currentTime + 0.01);
    masterGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.2);
    masterGain.connect(ctx.destination);

    // ── Note sequence: ascending musical phrase ──
    // C5 → E5 → G5 → C6  (like a rising "done!" chime)
    const notes: Array<{ freq: number; start: number; dur: number; vol: number }> = [
      { freq: 523.25, start: 0.0,  dur: 0.18, vol: 0.55 },  // C5
      { freq: 659.25, start: 0.12, dur: 0.18, vol: 0.55 },  // E5
      { freq: 783.99, start: 0.24, dur: 0.18, vol: 0.55 },  // G5
      { freq: 1046.5, start: 0.36, dur: 0.55, vol: 0.75 },  // C6 (hold)
      // Harmony: add a soft E6 underneath the hold
      { freq: 1318.5, start: 0.5,  dur: 0.35, vol: 0.25 },  // E6 (soft)
    ];

    notes.forEach(({ freq, start, dur, vol }) => {
      // Main sine tone
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);

      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(vol, ctx.currentTime + start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);

      osc.connect(gain);
      gain.connect(masterGain);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);

      // Triangle overtone for warmth
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(freq * 2, ctx.currentTime + start);
      gain2.gain.setValueAtTime(0, ctx.currentTime + start);
      gain2.gain.linearRampToValueAtTime(vol * 0.15, ctx.currentTime + start + 0.015);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur * 0.7);
      osc2.connect(gain2);
      gain2.connect(masterGain);
      osc2.start(ctx.currentTime + start);
      osc2.stop(ctx.currentTime + start + dur);
    });

    // ── Shaker / sparkle effect at the end ──
    const bufferSize = ctx.sampleRate * 0.12;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * 0.3;
    }
    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'highpass';
    noiseFilter.frequency.setValueAtTime(6000, ctx.currentTime);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0, ctx.currentTime + 0.38);
    noiseGain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.42);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.58);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(masterGain);
    noiseSource.start(ctx.currentTime + 0.38);
    noiseSource.stop(ctx.currentTime + 0.62);

    // Close context after sound finishes
    setTimeout(() => {
      ctx.close().catch(() => { /* ignore */ });
    }, 2500);
  } catch {
    // Silently fail — sound is an enhancement only
  }
}
